import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMask } from "hooks/useMask";
import { useCaptchaV3 } from "hooks/useCaptchaV3";
import {
  InputLabel,
  ErrorMessage,
  Button,
  PasswordStrengthMeter,
  Loader
} from "components/UI/atoms";
import { PasswordTooltipHint } from "components/UI/molecules";
import { validationSchema } from "./utils/validationSchema";
import { useSignUpSubmit } from "./utils/useSignUpSubmit";
import { TermsOfUseModal } from "..";
import "./index.scss";

export const SignUpForm = ({ defaultValues = {} }) => {
  const { referralCode } = defaultValues;
  const { isLoading, submitSignUp, errors: errorsFromAPI } = useSignUpSubmit();
  const {
    register,
    handleSubmit,
    formState: { errors, touchedFields },
    trigger,
    watch,
    setValue,
    setError
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues
  });
  const { captchaToken } = useCaptchaV3("login");
  const { maskedValue: maskedPhone, onChange: onPhoneChange } =
    useMask("(###) ### - ####");
  const [passedValidations, setPassedValidations] = useState(0);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const passwordValue = watch("password");

  const onSubmit = async (data) =>
    await submitSignUp({ ...data, referralCode, captchaToken });

  const checkValidations = (password) => {
    let count = 0;
    if (password?.length >= 8) count++;
    if (/[A-Z]/.test(password)) count++;
    if (/[0-9]/.test(password)) count++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) count++;
    setPassedValidations(count);
  };

  const handlePhoneChange = (e) => {
    onPhoneChange(e);
    setValue("phone", e.target.value);
  };

  useEffect(() => {
    checkValidations(passwordValue);
  }, [passwordValue]);

  useEffect(() => {
    if (!errorsFromAPI?.length) return;

    errorsFromAPI.forEach((item) => {
      setError(item.name, {
        type: "manual",
        message: item.value
      });
    });
  }, [errorsFromAPI, setError]);

  return (
    <form
      id="signup-form"
      className="signup-form"
      onSubmit={handleSubmit(onSubmit, () => trigger())}
    >
      <SignUpFormField
        placeholder="Email Address"
        label="Email"
        id="email"
        type="email"
        error={errors.email}
        register={register}
        trigger={trigger}
        showValidationIcon={errors.email || touchedFields.email}
        disabled={!!defaultValues.email}
        disablePopUp
        required
      />

      <SignUpFormField
        value={maskedPhone}
        placeholder="( ### ) ### - ####"
        label="Phone Number"
        id="phone"
        type="tel"
        error={errors.phone}
        register={register}
        onChange={handlePhoneChange}
        trigger={trigger}
        showValidationIcon={errors.phone || touchedFields.phone}
        disablePopUp
        required
      />
      <div className="password-container">
        <SignUpFormField
          placeholder="Password"
          label="Password"
          id="password"
          type="password"
          error={errors.password}
          register={register}
          trigger={trigger}
          showValidationIcon={errors.password || touchedFields.password}
          hintTooltip={<PasswordTooltipHint />}
          disablePopUp
          required
        />

        <PasswordStrengthMeter passwordScore={passedValidations} />
      </div>

      <SignUpFormField
        placeholder="Password"
        label="Confirm Password"
        id="confirmPassword"
        type="password"
        error={errors.confirmPassword}
        register={register}
        trigger={trigger}
        showValidationIcon={
          errors.confirmPassword || touchedFields.confirmPassword
        }
        disablePopUp
        required
      />

      {defaultValues.referralCode && (
        <SignUpFormField
          placeholder=""
          label="Referral Code"
          id="referralCode"
          type="text"
          error={errors.referralCode}
          register={register}
          trigger={trigger}
          disablePopUp
        />
      )}

      <p className="terms-of-use">
        By joining, I agree to GCX's{" "}
        <button
          type="button"
          className="terms-of-use-button"
          onClick={() => setModalIsOpen(true)}
        >
          Terms of Use.
        </button>
      </p>

      <Button
        customClassName={`continue-button ${
          isLoading ? "continue-button-loading" : ""
        }`}
        type="submit"
        disabled={isLoading}
        aria-label="Register"
      >
        {isLoading ? (
          <Loader variant="secondary" id="loader-button" />
        ) : (
          "Continue"
        )}
      </Button>

      <TermsOfUseModal
        modalIsOpen={modalIsOpen}
        setModalIsOpen={setModalIsOpen}
      />
    </form>
  );
};

const SignUpFormField = ({
  label,
  error,
  register,
  trigger,
  id,
  type = "text",
  showValidationIcon,
  hintTooltip = null,
  disablePopUp,
  required = false,
  ...props
}) => (
  <div className="field-container">
    <InputLabel
      label={label}
      id={id}
      hasError={!!error}
      type={type}
      showValidationIcon={showValidationIcon}
      {...register(id, { required: required })}
      onBlur={(e) => {
        register(id).onBlur(e);
        trigger(id);
      }}
      hintTooltip={hintTooltip}
      disablePopUp={disablePopUp}
      required={required}
      {...props}
    />
    {error && <ErrorMessage error={error} />}
  </div>
);
